/**
 * Primitivas del design system — los valores crudos, en un solo lugar.
 *
 * Extraído de `theme-silia.ts` (paso 03 del plan, ver `theme/spec.md` §9).
 * Movimiento mecánico: ni un valor cambia. Esta es la hoja del grafo de
 * dependencias — no importa nada del tema.
 */

export const silliaLogo = process.env.PUBLIC_URL + '/assets/icons/Logo-Silia.svg';
export const silliaLogoMobile = process.env.PUBLIC_URL + '/assets/icons/LogoMobileSilia.svg';

export const silliaBackground = process.env.PUBLIC_URL + '/assets/img/silia-background.png';
export const silliaBackgroundWebp = process.env.PUBLIC_URL + '/assets/img/silia-background.webp';
export const silliaHeroBackground = process.env.PUBLIC_URL + '/assets/img/BackgroundHome.png';
export const silliaHeroBackgroundWebp = process.env.PUBLIC_URL + '/assets/img/BackgroundHome.webp';
export const silliaHeroBackgroundWebpMobile =
  process.env.PUBLIC_URL + '/assets/img/BackgroundHome-mobile.webp';
export const silliaModulesBackground = process.env.PUBLIC_URL + '/assets/img/Background.png';
export const silliaModulesBackgroundWebp = process.env.PUBLIC_URL + '/assets/img/Background.webp';

/* ═══════════════════════════════════════════════════════════════════════
   1. PRIMITIVES — the raw values, in one place
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Brand + purple ramp. P3/P7 is the canonical pair: P3 is the tint a brand
 * surface gets, P7 the text that sits on it. P6 exists because the jump from
 * P5 to P7 was too wide to place a mid-weight border.
 */
/**
 * La rampa de marca: los diez valores que una marca redefine entera.
 *
 * Los nombres son de POSICIÓN, no de color. En Silia los neutrales son
 * violetas y en Traxión grises, y el mismo `n700` es el texto de acento en las
 * dos — que es exactamente lo que `purple7` no podía decir.
 */
export type BrandRamp = {
  /** Las dos anclas de marca. No son pasos de la rampa: `lime` es el CTA y
   *  `black` la tinta más fuerte, y una marca los elige con independencia de
   *  sus neutrales (Traxión: lima y grises). */
  black: string;
  lime: string;
  /**
   * Los neutrales de acento, en escala 100-900 por luminancia.
   *
   * Falta el 300 a propósito. La rampa heredada tenía saltos muy desparejos
   * —de 0.022 a 0.241 de luminancia— y numerarla 1..8 habría prometido una
   * escala uniforme que no existe. El hueco es información: dice dónde entra
   * un paso si algún día hace falta, en vez de esconder que el salto de 200 a
   * 400 es el más grande de la rampa.
   */
  n100: string;
  n200: string;
  n400: string;
  n500: string;
  n600: string;
  n700: string;
  n800: string;
  n900: string;
};

export const brand: BrandRamp = {
  black: '#21212f',
  lime: '#deff38',
  n100: '#f5f0ff',
  n200: '#ebe3ff',
  n400: '#cdbbff',
  n500: '#a493d9',
  n600: '#7d6ec8',
  n700: '#5b518c',
  n800: '#3d3667',
  n900: '#2a2550',
};

/**
 * Surfaces, from the canvas up. `bg-layout` is the app background, `elevated`
 * the sidebar, `container` a card. The three tints are for banding inside a
 * container — a striped row, a disabled cell — where a border would be too loud.
 */
export const surface = {
  bgLayout: '#eff0eb',
  bgElevated: '#fbfbf8',
  bgContainer: '#ffffff',
  tint1: '#fbfbfb',
  tint2: '#f6f6f7',
  tint3: '#ededee',
  inputAddon: '#f7f7f6',
} as const;

/**
 * Text and icons — flat hex, never rgba. Two levels of text, not three: between
 * the primary (10.87:1 on white) and the AA floor there is only room for two
 * distinguishable steps. `textIcon` is for glyphs ONLY — the prototype's audit
 * found it used as a text colour in 356 rules at 2.76:1, below even the
 * minimum for a chart.
 *
 * `textDescription` is #6a6a73, not #6f6f78: the lighter one failed AA on the
 * app canvas (4.34:1 on #eff0eb). This one passes on all three surfaces —
 * white 5.35, canvas 4.67, tint-3 4.58 — and the difference on white is
 * imperceptible.
 */
export const ink = {
  text: '#3c3c48',
  textDescription: '#6a6a73',
  textIcon: '#9b9ba1',
  textPlaceholder: '#c8c8cb',
  /**
   * Text on a coloured fill, not on a surface. Matches `bgContainer` in value
   * but not in meaning: the white of a purple button is not "the container
   * colour", and a template could want them apart.
   */
  textInverse: '#ffffff',
} as const;

export const line = {
  border: '#dedee0',
  borderSubtle: '#f2f2f3',
  /** Legacy aliases of `borderSubtle`, kept because existing CSS reads them. */
  fillContent: '#f2f2f3',
  split: '#f2f2f3',
} as const;

/**
 * Semantic feedback. `-fg` paints icons and solid fills; `-fgStrong` is text on
 * the matching `-bg` — the darkest step that still reads as its hue and passes
 * AA. Only `success` used to have one, so every screen needing a dark red or
 * amber invented its own: the prototype's audit found six parallel reds, six
 * ambers and five greens.
 */
export const semantic = {
  successBg: '#f6ffed',
  successFg: '#52c41a',
  successFgStrong: '#135200',
  successBd: '#b7eb8f',
  warningBg: '#fffbe6',
  warningFg: '#faad14',
  warningFgStrong: '#874d00',
  warningBd: '#ffe58f',
  dangerBg: '#fff1f0',
  dangerFg: '#ff4d4f',
  dangerFgStrong: '#cf1322',
  dangerBd: '#ffa39e',
  // `info` is the accent ramp, not Ant Design's blue. blue-1/6/3 used to sit here,
  // which is why an info callout was the one off-system surface on screens whose
  // chips, switches and buttons already read as Silia (QA on SL-1654).
  //
  // These are Silia's steps as literals, because two consumers cannot take a
  // `var()`: `tokens.colorInfo` feeds Ant Design's colour algorithm, which derives
  // hover/active shades and needs a parseable colour, and `components.Alert` is
  // guarded against var() strings for the same reason. Other brands override the
  // ramp through `buildComponents(accent)` — see `semanticFor()` below.
  //
  // Steps: bg n100, bd n400, fg n700 (7.00 with white on the filled badge, where
  // the old #1677ff gave 3.66 — under AA), fgStrong n800 (9.78 on n100, was 5.50).
  infoBg: brand.n100,
  infoFg: brand.n700,
  infoFgStrong: brand.n800,
  infoBd: brand.n400,
} as const;

/**
 * Data View tag extras. The six semantic pairs above cover Row Fill, Kanban
 * lanes and status-like tags; these four extend the shared picker, where
 * options are arbitrary vocabulary and need range beyond status. They sit in
 * the gaps the semantics leave around the wheel, so every tone is the only one
 * of its family. Never use them for state — that stays semantic.
 *
 * `-fg` values here are a darker step than the semantic `-fgStrong`, because
 * the `tag` weight is more opaque than the `bg`: there the general strong step
 * lands at 3.94:1 (red) and 4.21 (blue).
 */
export const tagExtras = {
  redFg: '#a8071a',
  blueFg: '#003eb3',
  /** Amber keeps its semantic bg/fg, but tags mix from a cleaner lemon — the
      warning token (#faad14) is an egg-yolk gold that sat on top of orange. */
  amberHue: '#fadb14',
  cyanBg: '#e6fffb',
  cyanFg: '#006d75',
  cyanHue: '#13c2c2',
  orangeBg: '#fff7e6',
  orangeFg: '#ad4e00',
  orangeHue: '#fa8c16',
  pinkBg: '#fff0f6',
  pinkFg: '#c41d7f',
  pinkHue: '#eb2f96',
  brownBg: '#f7f0e8',
  brownFg: '#8c5a2b',
  brownHue: '#a9714b',
} as const;

/** Call / conversation sentiment avatars only. Not a status scale. */
export const emotion = {
  positiveBg: '#e1fad6',
  positiveFg: '#aaf189',
  neutralBg: '#ffefc8',
  neutralFg: '#ffd261',
  negativeBg: '#ffcec1',
  negativeFg: '#ff734d',
} as const;

/**
 * Object-kind families — what an object IS (folder, agent, data view), not its
 * state nor a field value. Each is bg + fg + bd; the `fg` serves both the tile
 * glyph and the tag text, and every pair passes AA on its own bg. These used to
 * live as ~40 loose hex values in LogoBadge and quickAccess, so they did not
 * repaint with the template.
 */
export const kind = {
  folderBg: brand.n200,
  folderFg: brand.n700,
  folderBd: brand.n400,
  /** Agent is the only one with two levels: its tile is the CTA lime, which is
      too saturated for a text tag — hence the `soft` variant. */
  agentBg: brand.lime,
  agentFg: brand.black,
  agentBd: '#c7e437',
  agentSoftBg: '#f4ffcf',
  agentSoftFg: '#5a6b00',
  agentSoftBd: '#e0f59d',
  qaBg: '#ffcec1',
  qaFg: '#a33d22',
  qaBd: '#ffb3a1',
  rtaBg: '#c8efe8',
  rtaFg: '#166b5e',
  rtaBd: '#a3e0d5',
  dataViewBg: '#ffe0ec',
  dataViewFg: '#a14a76',
  dataViewBd: '#f5bfd2',
  dashboardBg: '#dde6ff',
  dashboardFg: '#4d5a99',
  dashboardBd: '#bcc8f2',
  fileBg: '#e8e7f0',
  fileFg: '#5b518c',
  fileBd: '#d6d3e6',
} as const;

/**
 * Process Flow canvas node types. Note that an agent here is amber, not lime:
 * lime is CTA-only, and a canvas full of lime nodes would read as a screen of
 * buttons. It is the one family that deliberately does not match `kind`.
 */
export const node = {
  toolBg: '#e8f1ff',
  toolFg: '#1b4a99',
  toolBd: '#2e6fd6',
  mcpBg: '#f0e9ff',
  mcpFg: '#4a2b9c',
  mcpBd: '#7a4fd6',
  tableBg: '#e4f7f0',
  tableFg: '#0f5e49',
  tableBd: '#0f9d78',
  agentBg: '#fdf3d9',
  agentFg: '#7a5610',
  agentBd: '#c78a1a',
} as const;

/**
 * Translucent fills and scrims. Here translucency is the mechanism, not a
 * shortcut: a hover fill has to let through whatever is underneath, which
 * changes depending on where the row lands. The prototype's audit found ~70
 * alphas invented on the spot; they are three families, by what they paint on.
 */
export const fill = {
  f1: 'rgba(33, 33, 47, 0.02)',
  f2: 'rgba(33, 33, 47, 0.06)',
  f3: 'rgba(33, 33, 47, 0.10)',
  brand1: 'rgba(91, 81, 140, 0.06)',
  brand2: 'rgba(91, 81, 140, 0.12)',
  inverse1: 'rgba(255, 255, 255, 0.08)',
  inverse2: 'rgba(255, 255, 255, 0.16)',
  scrim: 'rgba(33, 33, 47, 0.45)',
  scrimStrong: 'rgba(33, 33, 47, 0.72)',
  limeSoft: 'rgba(222, 255, 56, 0.18)',
  limeBorder: 'rgba(222, 255, 56, 0.6)',
  limeTenue: 'rgba(222, 255, 56, 0.3)',
} as const;

/**
 * Elevation. Four levels, not more: the card tile, the popover, the modal, and
 * the RTA's simulated-desktop windows which float over a wallpaper and need
 * that weight to read as detached. The prototype measured 172 distinct shadow
 * values across 314 sites before collapsing them here.
 *
 * `inset` shadows are not part of this scale — they draw a 1px border, not height.
 */
export const elevation = {
  sm: '0 2px 4px rgba(33, 33, 47, 0.02), 0 1px 6px rgba(33, 33, 47, 0.02), 0 1px 2px rgba(33, 33, 47, 0.03)',
  md: '0 6px 16px rgba(33, 33, 47, 0.06), 0 3px 6px rgba(33, 33, 47, 0.04)',
  lg: '0 12px 32px rgba(33, 33, 47, 0.12), 0 4px 8px rgba(33, 33, 47, 0.06)',
  xl: '0 18px 50px rgba(33, 33, 47, 0.32)',
  focusRing: `0 0 0 3px ${brand.n200}`,
  glow: `0 0 0 3px ${fill.limeTenue}`,
  /** The CTA glow lives outside the elevation scale because it does not suggest
      height — it tints the shadow with the button's own colour. */
  cta: '0 2px 6px rgba(222, 255, 56, 0.45)',
  ctaHover: '0 4px 10px rgba(222, 255, 56, 0.65)',
} as const;

/**
 * Typography. DM Sans is the only family in the product; the monospace is for
 * measured values (logs, ids, hex, code). The prototype had eight competing
 * stacks and referenced an undefined `--silia-font-mono`.
 */
export const typography = {
  fontSans: "'DM Sans', system-ui, -apple-system, sans-serif",
  fontMono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 14,
  fontSizeIcons: 24,
  /** 22/14 — the prototype's AntD `lineHeight`. */
  lineHeight: 22 / 14,

  /**
   * Headings.
   *
   * AntD derives its own ramp from `fontSize` and lands on 38/30/24/20/16.
   * The product's page title is 30px — the second step — so every screen that
   * wanted it reached for `<Title level={1}>` and then forced the size back
   * down: `.org-card__title`, `.sign-in-title` in Login and the same rule
   * copied into RecoverAccount all carried `font-size: 30px`, two of them with
   * `!important`.
   *
   * Naming the steps here keeps the level semantic — a page has one h1 — and
   * puts the size on the token. Only heading1 moves off AntD's default: it
   * becomes the 30px the product actually uses, so `level={1}` needs no CSS.
   */
  fontSizeHeading1: 30,
  fontSizeHeading2: 24,
  fontSizeHeading3: 20,
  fontSizeHeading4: 16,
  fontSizeHeading5: 14,

  /** Line heights as ratios, the unit AntD expects. 38/30, 32/24, 28/20… */
  lineHeightHeading1: 38 / 30,
  lineHeightHeading2: 32 / 24,
  lineHeightHeading3: 28 / 20,
  lineHeightHeading4: 24 / 16,
  lineHeightHeading5: 22 / 14,
} as const;

/* ═══════════════════════════════════════════════════════════════════════
   2. SHAPE & SPACE — derived, not copied
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Radius scale, derived from every `border-radius` in the prototype's CSS.
 * Measured usage:
 *
 *   12px → 495   the default: cards, inputs, buttons, modals' inner blocks
 *    8px → 389   the small step: tags, list rows, nested blocks
 *  999px → 256 ┐ pill
 * 9999px →  45 ┘ (same intent, two spellings — collapsed here)
 *    50% →  99   circle — avatars, dots
 *    6px →  92   almost entirely the Traxión template, whose DS base is 6
 *   16px →  63   modal shell
 *    4px →  23   the tightest step: swatches, micro-badges
 *
 * Everything below 20 uses (5px, 3px, 10px, 9px, 14px, 34px, 2px, 1px) is
 * noise, not intent — each is within 2px of a step above. They collapse to the
 * nearest one on migration.
 */
export const radius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  /** Traxión's base. Unused by Silia; kept so a template can select it. */
  compact: 6,
  pill: 999,
  circle: '50%',
} as const;

/**
 * Spacing scale — a clean multiple-of-4, derived from every `gap` and `padding`
 * in the prototype's CSS. Measured `gap` usage:
 *
 *    8px → 754      16px → 183      32px →   9
 *   12px → 397      20px →  31      40px →   3
 *    4px → 315      24px →  26
 *
 * `padding` agrees: the top pairs are `8px 12px` (119), `12px 16px` (106),
 * `4px 8px` (83), `12px 12px` (79), `4px 12px` (74) — every component is
 * built out of 4/8/12/16/20/24.
 *
 * The stray 1px, 2px, 3px, 5px, 6px, 7px and 10px values are noise (≤13 uses
 * each) and snap to the nearest step. `xxl: 48` has almost no `gap` usage but
 * is the page-section rhythm the old theme already published, so it stays.
 */
export const space = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

/**
 * Control height, global. The prototype set it only on `Button` and `Input`, so
 * `Select` and `DatePicker` fell back to AntD's default: one Billing form had
 * fields at 32 (Select), 40 (own input) and 44 (DatePicker). Set at the token
 * level so every control inherits it.
 */
export const control = {
  height: 40,
  /** Toolbar height. Real, not a rounding: six hand-written families asked for
      it before AntD's `size="small"` was pointed at it. */
  heightSM: 32,
} as const;

/* ═══════════════════════════════════════════════════════════════════════
   3. TAG PALETTE — the one list for everything that colours something
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Ten tones, laid out as a spectrum (red → orange → yellow → green → cyan →
 * blue → purple → pink) with the two neutrals closing it, so a colour is always
 * in the same place in every picker. Deliberately not twelve: an earlier set
 * had indigo reading as a second blue and lime as a second green, which turns
 * picking into comparison instead of recognition.
 *
 * Each tone carries BOTH contracts, on purpose:
 *
 *   `bg` / `fg` / `hue` — the prototype's shape. Values are `var(--silia-*)`
 *     strings, so a tone repaints when the template changes. This is what new
 *     code should read.
 *
 *   `fill` / `tag` / `dot` / `text` — the shape `theme-silia-antiguo` exports
 *     and `features/data-views/utils/tagColors.ts` consumes today, as flat hex.
 *     Kept so Data Views keeps compiling the moment this file is wired up;
 *     delete once that module is migrated.
 *
 * The two are not redundant weights of the same thing — `fill` is the near-white
 * Row Fill tint and `bg` is its CSS-var equivalent, while `tag` and `dot` are
 * intermediate opacities the prototype now mixes at render time from `hue`.
 * They are stored flat rather than derived so a tag keeps its colour when
 * composited over an already-filled row.
 */
export const tagPalette = [
  {
    key: 'red',
    label: 'Red',
    bg: 'var(--silia-danger-bg)',
    fg: 'var(--silia-tag-red-fg)',
    hue: 'var(--silia-danger-fg)',
    fill: '#FFF6F5',
    tag: '#FFCDCD',
    dot: '#FF8B8C',
    text: '#FF4D4F',
  },
  {
    key: 'orange',
    label: 'Orange',
    bg: 'var(--silia-tag-orange-bg)',
    fg: 'var(--silia-tag-orange-fg)',
    hue: 'var(--silia-tag-orange-hue)',
    fill: '#FFFAEF',
    tag: '#FEDFB8',
    dot: '#FCB565',
    text: '#D46B08',
  },
  {
    key: 'amber',
    label: 'Yellow',
    bg: 'var(--silia-warning-bg)',
    fg: 'var(--silia-warning-fg-strong)',
    hue: 'var(--silia-tag-amber-hue)',
    fill: '#FFFCEF',
    tag: '#FEF4B8',
    dot: '#FCE764',
    text: '#FAAD14',
  },
  {
    key: 'green',
    label: 'Green',
    bg: 'var(--silia-success-bg)',
    fg: 'var(--silia-success-fg-strong)',
    hue: 'var(--silia-success-fg)',
    fill: '#F9FFF3',
    tag: '#D2F2BF',
    dot: '#90DA6A',
    text: '#135200',
  },
  {
    key: 'cyan',
    label: 'Cyan',
    bg: 'var(--silia-tag-cyan-bg)',
    fg: 'var(--silia-tag-cyan-fg)',
    hue: 'var(--silia-tag-cyan-hue)',
    fill: '#EFFFFC',
    tag: '#B8F2EE',
    dot: '#63D9D8',
    text: '#08979C',
  },
  {
    key: 'blue',
    label: 'Blue',
    bg: 'var(--silia-info-bg)',
    fg: 'var(--silia-tag-blue-fg)',
    hue: 'var(--silia-info-fg)',
    fill: '#EFF8FF',
    tag: '#B8D9FF',
    dot: '#65A6FF',
    text: '#1677FF',
  },
  {
    key: 'purple',
    label: 'Purple',
    bg: 'var(--silia-accent-bg)',
    fg: 'var(--silia-accent-fg)',
    hue: 'var(--silia-accent-border-strong)',
    fill: '#F2EDFF',
    tag: '#DBD1F7',
    dot: '#BFB1E7',
    text: '#5B518C',
  },
  {
    key: 'pink',
    label: 'Pink',
    bg: 'var(--silia-tag-pink-bg)',
    fg: 'var(--silia-tag-pink-fg)',
    hue: 'var(--silia-tag-pink-hue)',
    fill: '#FFF5F9',
    tag: '#FBC6E1',
    dot: '#F378BA',
    text: '#C41D7F',
  },
  {
    key: 'brown',
    label: 'Brown',
    bg: 'var(--silia-tag-brown-bg)',
    fg: 'var(--silia-tag-brown-fg)',
    hue: 'var(--silia-tag-brown-hue)',
    fill: '#FAF5F0',
    tag: '#E6D4C5',
    dot: '#C7A187',
    text: '#8C5A2B',
  },
  {
    key: 'gray',
    label: 'Gray',
    bg: 'var(--silia-fill-content)',
    fg: 'var(--silia-text-description)',
    hue: 'var(--silia-text-icon)',
    fill: '#F7F7F7',
    tag: '#DFDFE1',
    dot: '#BCBCC0',
    text: '#6F6F78',
  },
] as const;

export type TagColorKey = (typeof tagPalette)[number]['key'];
